import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, CircleAlert, LoaderCircle, ReceiptText } from "lucide-react";
import { useMemo, useState } from "react";

import { api, type PayableList } from "@/client";
import { MetricStrip } from "@/components/MetricStrip";
import { TransactionFiscalDocuments } from "@/components/TransactionFiscalDocuments";
import { INBOX_SOURCE_LABELS } from "@/domain/inbox";
import { FinancePresentation } from "@/presentation/finance";

type Obligation = PayableList["obligations"][number];
type PayableViewState = "OPEN" | "AWAITING_BANK" | "PAID_RECONCILED";

export function PayablesView() {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [state, setState] = useState<PayableViewState>("OPEN");
	const [expandedIds, setExpandedIds] = useState<string[]>([]);
	const [feedback, setFeedback] = useState<string>();
	const query = useQuery({
		queryKey: ["payables"],
		queryFn: () => api.payables.list(),
	});
	const reportMutation = useMutation({
		mutationFn: (input: { obligationId: string; paidOn: string }) =>
			api.payables.reportPayment(input),
		onSuccess: async () => {
			setFeedback(
				"Pagamento informado. Os movimentos já recebidos foram conferidos; se a saída ainda não estiver disponível, ela será conciliada após a próxima atualização bancária.",
			);
			await queryClient.invalidateQueries({ queryKey: ["payables"] });
		},
	});
	const obligations = useMemo(() => {
		if (!query.data) {
			return [];
		}

		const normalized = search.trim().toLocaleLowerCase("pt-BR");

		return query.data.obligations.filter(
			(obligation) =>
				(state === "AWAITING_BANK"
					? obligation.state === "AWAITING_BANK" || obligation.state === "REVIEW"
					: obligation.state === state) &&
				(!normalized ||
					`${obligation.creditorName} ${obligation.supplier?.name ?? ""} ${obligation.destination} ${obligation.expenseCategory?.name ?? ""}`
						.toLocaleLowerCase("pt-BR")
						.includes(normalized)),
		);
	}, [query.data, search, state]);

	if (query.isPending) {
		return (
			<div className="grid min-h-80 place-items-center text-sm font-medium text-stone-500">
				<span className="inline-flex items-center gap-2">
					<LoaderCircle className="size-4 animate-spin" />
					Abrindo o A Pagar…
				</span>
			</div>
		);
	}

	if (query.error) {
		return (
			<div className="rounded-3xl border border-red-200 bg-white p-8 text-center">
				<CircleAlert className="mx-auto size-8 text-red-600" />
				<p className="mt-3 font-semibold text-stone-900">Não foi possível abrir o A Pagar</p>
				<p className="mt-1 text-sm text-stone-500">{query.error.message}</p>
			</div>
		);
	}

	const data = query.data;

	return (
		<>
			{feedback && (
				<div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-medium text-emerald-900">
					{feedback}
				</div>
			)}
			<div className="mb-4">
				<MetricStrip
					items={[
						{
							label: "A pagar",
							value: String(data.summary.openCount),
							detail: `${data.summary.overdueCount} vencidos`,
							tone: data.summary.overdueCount > 0 ? "attention" : "neutral",
						},
						{
							label: "Total a pagar",
							value: FinancePresentation.money(data.summary.openAmountCents),
							detail: `${data.summary.recognizedCount} reconhecidas no histórico`,
						},
						{
							label: "Aguardando banco",
							value: FinancePresentation.money(
								data.summary.awaitingBankAmountCents + data.summary.reviewAmountCents,
							),
							detail: `${data.summary.awaitingBankCount} aguardando · ${data.summary.reviewCount} revisar`,
							tone: data.summary.reviewCount > 0 ? "attention" : "neutral",
						},
						{
							label: "Pagos e conciliados",
							value: FinancePresentation.money(data.summary.reconciledAmountCents),
							detail: `${data.summary.reconciledCount} ligados ao banco`,
							tone: "success",
						},
					]}
				/>
			</div>

			<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="flex flex-col justify-between gap-4 border-b border-stone-200 px-5 py-5 lg:flex-row lg:items-end">
					<div className="flex flex-col gap-4">
						<div>
							<h2 className="font-semibold text-stone-950">Obrigações</h2>
							<p className="mt-1 text-xs leading-5 text-stone-500">
								Abertas, aguardando banco e pagas permanecem separadas sem perder o histórico.
							</p>
						</div>
						<div className="inline-flex w-fit rounded-xl bg-stone-100 p-1">
							<StateButton
								active={state === "OPEN"}
								onClick={() => setState("OPEN")}
								label="A pagar"
								count={data.summary.openCount}
							/>
							<StateButton
								active={state === "AWAITING_BANK"}
								onClick={() => setState("AWAITING_BANK")}
								label="Aguardando banco"
								count={data.summary.awaitingBankCount + data.summary.reviewCount}
							/>
							<StateButton
								active={state === "PAID_RECONCILED"}
								onClick={() => setState("PAID_RECONCILED")}
								label="Pagos e conciliados"
								count={data.summary.reconciledCount}
							/>
						</div>
					</div>
					<input
						type="search"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Buscar credor ou classificação"
						className="h-10 min-w-72 rounded-xl border border-stone-200 px-3 text-sm outline-none transition focus:border-emerald-700"
					/>
				</header>

				{obligations.length === 0 ? (
					<div className="px-5 py-14 text-center">
						<ReceiptText className="mx-auto size-8 text-stone-300" />
						<p className="mt-3 text-sm font-semibold text-stone-700">
							{state === "OPEN"
								? "Nenhuma obrigação aguardando pagamento"
								: state === "AWAITING_BANK"
									? "Nenhum pagamento aguardando o banco"
									: "Nenhuma obrigação paga e conciliada"}
						</p>
						<p className="mt-1 text-xs text-stone-500">
							{state === "OPEN"
								? "Os candidatos reconhecidos na Caixa aparecerão aqui."
								: state === "AWAITING_BANK"
									? "Pagamentos informados aparecerão aqui até a Pluggy comprovar a saída."
									: "Os pagamentos ligados de forma única permanecerão neste histórico."}
						</p>
					</div>
				) : (
					<div className="divide-y divide-stone-100">
						{obligations.map((obligation) => (
							<ObligationRow
								key={obligation.id}
								obligation={obligation}
								expanded={expandedIds.includes(obligation.id)}
								paymentAccount={data.paymentAccount}
								today={data.horizon.today}
								reporting={
									reportMutation.isPending &&
									reportMutation.variables?.obligationId === obligation.id
								}
								reportError={
									reportMutation.variables?.obligationId === obligation.id
										? reportMutation.error
										: null
								}
								onToggle={() =>
									setExpandedIds((current) =>
										current.includes(obligation.id)
											? current.filter((id) => id !== obligation.id)
											: [...current, obligation.id],
									)
								}
								onReport={(paidOn) => {
									setFeedback(undefined);
									reportMutation.mutate({ obligationId: obligation.id, paidOn });
								}}
							/>
						))}
					</div>
				)}
			</section>
		</>
	);
}

function ObligationRow({
	obligation,
	expanded,
	paymentAccount,
	today,
	reporting,
	reportError,
	onToggle,
	onReport,
}: {
	obligation: Obligation;
	expanded: boolean;
	paymentAccount: PayableList["paymentAccount"];
	today: string;
	reporting: boolean;
	reportError: Error | null;
	onToggle: () => void;
	onReport: (paidOn: string) => void;
}) {
	const linkedDocuments = obligation.documents.filter(
		(document) => document.associationState === "LINKED",
	);
	const suggestedDocuments = obligation.documents.filter(
		(document) => document.associationState === "SUGGESTED",
	);

	return (
		<article>
			<button
				type="button"
				aria-expanded={expanded}
				onClick={onToggle}
				className="grid w-full gap-3 px-5 py-4 text-left transition hover:bg-stone-50 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center"
			>
				<div className="min-w-0">
					<p className="truncate text-sm font-semibold text-stone-900">
						{obligation.supplier?.name ?? obligation.creditorName}
					</p>
					<p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-stone-500">
						{obligation.payment ? (
							<span className="font-semibold text-emerald-700">
								Pago em {FinancePresentation.transactionDate(obligation.payment.occurredOn)}
							</span>
						) : obligation.paymentReport ? (
							<span
								className={
									obligation.state === "REVIEW"
										? "font-semibold text-amber-700"
										: "font-semibold text-sky-700"
								}
							>
								Informado em {FinancePresentation.transactionDate(obligation.paymentReport.paidOn)}
							</span>
						) : (
							<span className={obligation.overdue ? "font-semibold text-amber-700" : ""}>
								{obligation.overdue ? "Venceu" : "Vence"}{" "}
								{FinancePresentation.transactionDate(obligation.dueOn)}
							</span>
						)}
						<span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
							{obligation.destination}
							{obligation.expenseCategory ? ` → ${obligation.expenseCategory.name}` : ""}
						</span>
						<span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
							{INBOX_SOURCE_LABELS[obligation.source]}
						</span>
						{obligation.payment && (
							<span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
								Pago e conciliado
							</span>
						)}
						{obligation.state === "AWAITING_BANK" && (
							<span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
								Aguardando banco
							</span>
						)}
						{obligation.state === "REVIEW" && (
							<span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
								Revisar pagamento
							</span>
						)}
					</p>
				</div>
				<div className="flex items-center justify-between gap-4 lg:justify-end">
					<p className="text-sm font-semibold tabular-nums text-stone-950">
						{FinancePresentation.money(obligation.amountCents)}
					</p>
					<ChevronDown
						className={`size-4 text-stone-500 transition-transform ${expanded ? "rotate-180" : ""}`}
					/>
				</div>
			</button>

			{expanded && (
				<div className="border-t border-stone-100 bg-stone-50/60 px-5 py-5">
					<div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
						<div>
							<h3 className="text-sm font-semibold text-stone-900">Obrigação reconhecida</h3>
							<dl className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
								<Fact label="Credor" value={obligation.creditorName} />
								<Fact
									label="Identidade fiscal"
									value={
										obligation.creditorTaxId
											? FinancePresentation.taxId(obligation.creditorTaxId)
											: "Não informada"
									}
								/>
								<Fact label="Classificação" value={obligation.destination} />
								<Fact
									label="Categoria"
									value={obligation.expenseCategory?.name ?? "Não se aplica"}
								/>
								<Fact label="Reconhecido por" value={obligation.recognizedBy} />
								<Fact
									label="Reconhecido em"
									value={FinancePresentation.moment(obligation.recognizedAt)}
								/>
							</dl>
							<p className="mt-4 rounded-2xl border border-stone-200 bg-white p-4 text-sm leading-6 text-stone-700">
								{obligation.description}
							</p>
							{obligation.state === "OPEN" && (
								<PaymentReportForm
									account={paymentAccount}
									defaultPaidOn={today}
									pending={reporting}
									error={reportError}
									onReport={onReport}
								/>
							)}
							{obligation.paymentReport && (
								<div
									className={`mt-4 rounded-2xl border p-4 ${
										obligation.state === "REVIEW"
											? "border-amber-200 bg-amber-50"
											: "border-sky-200 bg-sky-50"
									}`}
								>
									<p className="text-xs font-semibold text-stone-900">
										{obligation.state === "REVIEW"
											? "Pagamento exige revisão"
											: obligation.payment
												? "Pagamento informado"
												: "Pagamento informado — aguardando banco"}
									</p>
									<dl className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
										<Fact
											label="Data informada"
											value={FinancePresentation.transactionDate(obligation.paymentReport.paidOn)}
										/>
										<Fact
											label="Conta"
											value={`${paymentAccount.name}${paymentAccount.number ? ` · ${paymentAccount.number}` : ""}`}
										/>
										<Fact label="Informado por" value={obligation.paymentReport.reportedBy} />
										<Fact
											label="Informado em"
											value={FinancePresentation.moment(obligation.paymentReport.reportedAt)}
										/>
									</dl>
									{obligation.state === "REVIEW" && (
										<p className="mt-3 text-xs leading-5 text-amber-800">
											Existe movimento bancário plausível na data informada, mas fornecedor, valor
											ou unicidade ainda não fecham a conciliação.
										</p>
									)}
								</div>
							)}
							{obligation.payment && (
								<div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
									<p className="text-xs font-semibold text-emerald-900">
										Pagamento bancário conciliado
									</p>
									<dl className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
										<Fact
											label="Data bancária"
											value={FinancePresentation.transactionDate(obligation.payment.occurredOn)}
										/>
										<Fact
											label="Valor pago"
											value={FinancePresentation.money(obligation.payment.amountCents)}
										/>
										<Fact
											label="Recebedor"
											value={obligation.payment.receiverName ?? "Não informado"}
										/>
										<Fact
											label="Meio"
											value={obligation.payment.paymentMethod ?? "Não informado"}
										/>
									</dl>
									<p className="mt-3 text-xs leading-5 text-emerald-800">
										{obligation.payment.description}. Vínculo único por fornecedor, valor e data
										exatos.
									</p>
								</div>
							)}
						</div>
						<div>
							<h3 className="text-sm font-semibold text-stone-900">Documentos</h3>
							<div className="mt-3">
								<TransactionFiscalDocuments
									documents={linkedDocuments}
									showEmpty={suggestedDocuments.length === 0}
									emptyExplanation="Nenhum documento fiscal foi ligado a esta obrigação."
									linkExplanation={
										obligation.payment
											? "Documento preservado desde a Caixa; a conciliação financeira liga a obrigação ao banco sem transformar este vínculo em conciliação fiscal."
											: "Documento preservado desde a Caixa de entrada; o vínculo ainda não representa pagamento ou conciliação."
									}
								/>
							</div>
							{suggestedDocuments.length > 0 && (
								<div className="mt-4">
									<p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
										Para conferir
									</p>
									<TransactionFiscalDocuments
										documents={suggestedDocuments}
										linkExplanation="Possível documento preservado para conferência; o reconhecimento da obrigação não confirmou este vínculo."
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</article>
	);
}

function PaymentReportForm({
	account,
	defaultPaidOn,
	pending,
	error,
	onReport,
}: {
	account: PayableList["paymentAccount"];
	defaultPaidOn: string;
	pending: boolean;
	error: Error | null;
	onReport: (paidOn: string) => void;
}) {
	const [paidOn, setPaidOn] = useState(defaultPaidOn);

	return (
		<div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4">
			<p className="text-xs font-semibold text-sky-950">Pagamento realizado no banco?</p>
			<p className="mt-1 text-xs leading-5 text-sky-800">
				Informe a execução para verificarmos a saída na Pluggy. Isso ainda não declara conciliação.
			</p>
			<div className="mt-3 grid gap-3 sm:grid-cols-2">
				<label className="text-xs font-medium text-stone-700">
					Data do pagamento
					<input
						type="date"
						value={paidOn}
						min={`${defaultPaidOn.slice(0, 7)}-01`}
						max={defaultPaidOn}
						onChange={(event) => setPaidOn(event.target.value)}
						className="mt-1.5 h-10 w-full rounded-xl border border-sky-200 bg-white px-3 text-sm outline-none focus:border-emerald-700"
					/>
				</label>
				<div className="text-xs font-medium text-stone-700">
					Conta utilizada
					<p className="mt-1.5 flex h-10 items-center rounded-xl border border-sky-200 bg-white px-3 text-sm font-semibold text-stone-800">
						{account.name}
						{account.number ? ` · ${account.number}` : ""}
					</p>
				</div>
			</div>
			{error && <p className="mt-3 text-xs text-red-700">{error.message}</p>}
			<button
				type="button"
				disabled={pending || !paidOn}
				onClick={() => onReport(paidOn)}
				className="mt-3 inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-sky-900 px-4 text-xs font-semibold text-white transition hover:bg-sky-800 disabled:cursor-wait disabled:opacity-60"
			>
				{pending && <LoaderCircle className="size-3.5 animate-spin" />}
				Informar pagamento realizado
			</button>
		</div>
	);
}

function StateButton({
	active,
	onClick,
	label,
	count,
}: {
	active: boolean;
	onClick: () => void;
	label: string;
	count: number;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
				active ? "bg-white text-stone-950 shadow-sm" : "text-stone-500 hover:text-stone-800"
			}`}
		>
			{label} · {count}
		</button>
	);
}

function Fact({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-[10px] font-medium uppercase tracking-wide text-stone-400">{label}</dt>
			<dd className="mt-0.5 font-semibold text-stone-700">{value}</dd>
		</div>
	);
}
